import { createGqlFetcher } from 'app/lib/gqlFetcher'
import classNames from 'classnames'
import Button from 'app/components/Button'
import FormCombo from 'app/components/form/FormCombo'
import Spinner from 'app/components/Spinner'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormInput from 'app/components/form/FormInput'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { SmartForm } from 'app/components/form/SmartForm'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import type { LocationJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { OptionType } from 'types/OptionType'
import { dniValidator, extractAreaCode } from './dniSchema'
import { PURCHASE_OPTION_ID, type UseDniFormReturn } from './useDniForm'

type AutoScaleField = {
  name: string
  label: string
  defaultValue: string
  placeholder?: string
  hint: string
  onChange?: (value: string) => void
}

export type DniFormViewProps = UseDniFormReturn

export function DniFormView(props: DniFormViewProps) {
  const { isEditing, mode, location, dniData, maxPoolSizeValue, poolSizeValue, areaCode } = props
  const { removing, closing, submitting, busy, loadingValues, newDniId, numberOptions } = props
  const { selectedNumber, comboValue, snippetCode, locationName, locationId, dniId } = props
  const { setMode, setLocation, setMaxPoolSizeValue, setPoolSizeValue, setAreaCode, setSelectedNumber } = props
  const { onSubmitStart, onCreateCompleted, onDisable, onPurchaseNumber } = props

  const autoScaleActive = Number(maxPoolSizeValue) > Number(poolSizeValue || (isEditing ? `0` : `4`))

  const autoScaleFields: AutoScaleField[] = [
    {
      name: `maxPoolSize`,
      label: `Max With Auto-scale`,
      defaultValue: isEditing && dniData?.maxPoolSize ? dniData.maxPoolSize.toString() : ``,
      placeholder: `Off`,
      hint: `Up to 15.`,
      onChange: setMaxPoolSizeValue,
    },
    {
      name: `minFree`,
      label: `Free Threshold`,
      defaultValue: isEditing && dniData?.minFree ? dniData.minFree.toString() : `1`,
      hint: `Buy when free drops below this (1–3).`,
    },
    {
      name: `buyStep`,
      label: `Buy Step`,
      defaultValue: isEditing && dniData?.buyStep ? dniData.buyStep.toString() : `1`,
      hint: `Numbers to buy each time.`,
    },
    {
      name: `minPoolSize`,
      label: `Release Floor`,
      defaultValue: isEditing && dniData?.minPoolSize ? dniData.minPoolSize.toString() : ``,
      placeholder: `Pool size`,
      hint: `Never release below this. Defaults to pool size.`,
    },
  ]

  return (
    <>
      <SmartForm
        id="dni-form"
        className="mt-0! w-150 grow-0 justify-start gap-y-4! px-6 py-4"
        validator={dniValidator}
        reloadDocument={false}
        shouldPreventDefault={true}
        onSubmit={onSubmitStart}
        onPreventDefault={(data) => onCreateCompleted(data)}
        action="/api/integrations/dni"
        hideButtons={true}
        method="POST"
        hiddenInputs={
          isEditing
            ? [
                { name: `dniId`, value: dniId },
                { name: `locationId`, value: locationId! },
              ]
            : [
                { name: `id`, value: newDniId },
                { name: `locationId`, value: location?.value ?? `` },
              ]
        }
      >
        {isEditing ? (
          <div className="col-span-6 rounded-md border border-default bg-surface-subtle px-3 py-2">
            <p className="text-body-sm text-subtle">
              Location:{` `}
              <span className="font-medium text-strong">{locationName}</span>
            </p>
          </div>
        ) : (
          <FormComboAsync
            cols={6}
            name="_location"
            required={true}
            showSearchInDropdown={true}
            label="Location"
            value={location}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}

        <Input type="hidden" variant="bare" name="forwardTo" value={selectedNumber?.value || ``} />
        {location?.value ? (
          <FormCombo
            cols={6}
            name="_number"
            label="Number to Replace (swapped on website)"
            options={numberOptions}
            value={comboValue}
            placeholder="Select a number"
            labelClassName="text-body-sm text-strong"
            onChange={(val: OptionType) => {
              if (val.id === PURCHASE_OPTION_ID) {
                onPurchaseNumber()
                return
              }
              setSelectedNumber(val)
              setAreaCode(extractAreaCode(String(val.value)))
            }}
          />
        ) : (
          <div className="col-span-6">
            <Label className="text-body-sm text-strong">Number to Replace (swapped on website)</Label>
            <div className="mt-1 flex h-10 cursor-not-allowed items-center rounded-lg border border-default bg-surface-subtle px-4 text-body-sm text-minimal">
              Select a location first
            </div>
          </div>
        )}

        {!isEditing && (
          <FormInput
            type="text"
            cols={6}
            name="areaCode"
            label="Pool Area Code (3 digits)"
            labelClassName="text-body-sm text-strong"
            placeholder="e.g. 512"
            value={areaCode}
            maxLength={3}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ``).slice(0, 3))}
            newLineError={false}
            showErrorIcon
          />
        )}

        <FormInput
          type="text"
          cols={6}
          name="label"
          label="Display Name"
          labelClassName="text-body-sm text-strong"
          key={`label-${mode}-${dniData?.label}`}
          defaultValue={dniData?.label || ``}
          placeholder="e.g. Main Line Pool"
          newLineError={false}
          showErrorIcon
        />

        {loadingValues ? (
          <div className="col-span-6">
            <Label className="text-body-sm text-strong">Starting Pool Size</Label>
            <div className="mt-1 flex h-10 items-center">
              <Spinner size={4} />
            </div>
          </div>
        ) : (
          <FormInput
            type="number"
            cols={6}
            name="poolSize"
            label="Starting Pool Size (1–10)"
            labelClassName="text-body-sm text-strong"
            key={`poolSize-${mode}-${dniData?.poolSize}`}
            defaultValue={isEditing && dniData?.poolSize ? dniData.poolSize.toString() : `4`}
            onChange={(e) => setPoolSizeValue(e.target.value)}
            showErrorIcon
          />
        )}

        {!loadingValues ? (
          <div className="col-span-6 rounded-md border border-default bg-surface-subtle px-3 py-3">
            <div className="flex items-center gap-2">
              <span
                className={classNames(
                  `h-2 w-2 rounded-full`,
                  autoScaleActive ? `bg-success-default` : `bg-surface-strong`,
                )}
              />
              <div className="text-body-sm text-strong">Auto-scale is {autoScaleActive ? `on` : `off`}</div>
            </div>
            <p className="mt-0.5 text-xs text-subtle">
              Auto-scale buys more tracking numbers when the pool runs low. Each number is a paid phone number. Set a
              max above the starting pool size to turn it on, or leave it blank to keep the pool fixed.
            </p>
            <div className="mt-3 grid grid-cols-6 items-start gap-x-4 gap-y-3">
              {autoScaleFields.map((field) => (
                <div key={field.name} className="col-span-2 flex flex-col gap-y-1">
                  <FormInput
                    type="number"
                    cols={6}
                    name={field.name}
                    label={field.label}
                    labelClassName="block min-h-10 text-body-sm text-strong"
                    key={`${field.name}-${mode}-${field.defaultValue}`}
                    defaultValue={field.defaultValue}
                    placeholder={field.placeholder}
                    onChange={field.onChange ? (e) => field.onChange!(e.target.value) : undefined}
                    newLineError={false}
                    showErrorIcon
                  />
                  <span className="min-h-8 text-ui-xs text-subtle">{field.hint}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="col-span-6 rounded-md border border-default bg-surface-subtle px-3 py-3">
            <div className="text-body-sm text-strong">Auto-scale</div>
            <p className="mt-0.5 text-xs text-subtle">
              Auto-scale buys more tracking numbers when the pool runs low. Each number is a paid phone number. Set a
              max above the starting pool size to turn it on, or leave it blank to keep the pool fixed.
            </p>
            <div className="mt-3 grid grid-cols-6 items-start gap-x-4 gap-y-3">
              {autoScaleFields.map((field) => (
                <div key={field.name} className="col-span-2 flex flex-col gap-y-1">
                  <Label className="block min-h-10 text-body-sm text-strong">{field.label}</Label>
                  <div className="flex h-10 items-center">
                    <Spinner size={4} />
                  </div>
                  <span className="min-h-8 text-ui-xs text-subtle">{field.hint}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingValues ? (
          <div className="col-span-6">
            <Label className="text-body-sm text-strong">Visitor Session Duration (minutes)</Label>
            <div className="mt-1 flex h-10 items-center">
              <Spinner size={4} />
            </div>
          </div>
        ) : (
          <FormInput
            type="number"
            cols={6}
            name="sessionDuration"
            label="Session Duration in minutes (1–60)"
            labelClassName="text-body-sm text-strong"
            key={`session-${mode}-${dniData?.sessionDuration}`}
            defaultValue={isEditing && dniData?.sessionDuration ? dniData.sessionDuration.toString() : `30`}
            showErrorIcon
          />
        )}

        <div className="col-span-6">
          <Label className="text-body-sm text-strong">Embed Code</Label>
          <div className="mt-1 flex items-center gap-2 rounded-md border border-default bg-surface-subtle px-3 py-2">
            <code className="flex-1 text-xs text-default">
              {snippetCode || `Select a location to generate embed code`}
            </code>
            {snippetCode && <CopyToClipboard text={snippetCode} />}
          </div>
          <p className="mt-1 text-xs text-subtle">
            Add this script tag to every page of the dental office&apos;s website.
          </p>
        </div>
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {isEditing && dniData && (dniData.poolSize ?? 0) > 0 && (
              <Button
                label="Disable Pool"
                mode="danger"
                size="md"
                onClick={() => onDisable()}
                loading={removing || closing}
                disabled={busy}
              />
            )}
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <Button label="Cancel" mode="white" size="md" onClick={() => setMode(`summary`)} disabled={busy} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} disabled={busy} />
            )}
            <Button
              type="submit"
              form="dni-form"
              label="Save"
              mode="primary"
              size="md"
              loading={submitting || closing}
              disabled={busy}
            />
          </div>
        </div>
      </div>
    </>
  )
}
